import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpError } from 'app/helpers/httpError';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { LoaderService } from 'app/services/system/loader/loader.service';
import {
    CreateInterestRegisterEntryReq,
    InterestRegisterAttachment,
    InterestRegisterDetail,
    InterestRegisterEntryStatus,
    InterestRegisterEntryType,
    InterestRegisterListItem,
} from 'common/constants/assists-interest-register.constants';
import { environment } from 'environments/environment';
import { Observable, Subject } from 'rxjs';

interface ResultData<T> {
    status?: unknown;
    data?: T;
    requestStatus?: number;
    returnItem?: T;
}

interface CreateInterestRegisterEntryApiReq {
    entryType: number;
    formData: Record<string, unknown>;
}

interface CreateInterestRegisterEntryApiRes {
    entryGuid: string;
}

interface ListInterestRegisterEntryApiRes {
    entryGuid: string;
    entryType: number;
    createdDate: string;
    createdBy: number;
    createdByName?: string;
    isReviewed: boolean;
    reviewedDate?: string;
    reviewedBy?: number;
    hasAttachments: boolean;
}

interface ReadInterestRegisterEntryApiRes {
    entryGuid: string;
    entryType: number;
    formVersion: number;
    createdDate: string;
    createdBy: number;
    createdByName?: string;
    isReviewed: boolean;
    reviewedDate?: string;
    reviewedBy?: number;
    reviewedByName?: string;
    formData: Record<string, unknown>;
    attachments: InterestRegisterAttachmentApiRes[];
}

interface InterestRegisterAttachmentApiRes {
    attachmentGuid: string;
    fileName: string;
    fileExtension?: string;
    contentType?: string;
    fileSizeBytes: number;
}

interface ReadInterestRegisterAccessApiRes {
    canReadAll: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class InterestRegisterFacadeService {
    private readonly allowedAttachmentExtensions = [
        '.pdf',
        '.jpg',
        '.jpeg',
        '.png',
        '.doc',
        '.docx',
        '.xls',
        '.xlsx',
        '.msg',
        '.txt',
        '.eml',
    ];
    private readonly maxAttachmentCount = 5;
    private readonly maxAttachmentSizeBytes = 50 * 1024 * 1024; // 50 MB

    constructor(
        private readonly http: HttpClient,
        private readonly loaderService: LoaderService,
        private readonly alertsService: AlertsService,
        private readonly router: Router
    ) {}

    public createEntry(req: CreateInterestRegisterEntryReq): Observable<CreateInterestRegisterEntryApiRes> {
        const subject = new Subject<CreateInterestRegisterEntryApiRes>();
        const attachmentValidationError = this.getAttachmentValidationError(req.attachments ?? []);

        if (attachmentValidationError) {
            subject.error(attachmentValidationError);
            return subject.asObservable();
        }

        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Vista skráningu');

        const finish = (): void => {
            this.loaderService.changeActiveLoaders(-1);
        };

        const apiReq: CreateInterestRegisterEntryApiReq = {
            entryType: this.mapEntryTypeToApiValue(req.type),
            formData: this.normalizeFormDataForSubmit(req.type, req.formData),
        };

        this.http.post<ResultData<CreateInterestRegisterEntryApiRes>>(environment.localApi + 'InterestRegister/entries', apiReq).subscribe({
            next: (res) => {
                const createdEntry = res?.data;

                if (!createdEntry?.entryGuid || createdEntry.entryGuid === '00000000-0000-0000-0000-000000000000') {
                    finish();
                    subject.error('Skráning vistuð en auðkenni skráningar skilaði sér ekki rétt.');
                    return;
                }

                const attachments = req.attachments ?? [];

                if (attachments.length === 0) {
                    finish();
                    subject.next(createdEntry);
                    subject.complete();
                    return;
                }

                this.loaderService.changeLoaderMessage('Vista viðhengi');

                this.uploadAttachments(createdEntry.entryGuid, attachments).subscribe({
                    next: () => {
                        finish();
                        subject.next(createdEntry);
                        subject.complete();
                    },
                    error: (error) => {
                        finish();
                        subject.error(error);
                    },
                });
            },
            error: (error) => {
                finish();
                HttpError(this.alertsService, this.router, error, '', '', '', '');
                subject.error(error);
            },
        });

        return subject.asObservable();
    }

    public listMyEntries(): Observable<InterestRegisterListItem[]> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki mínar skráningar');

        const subject = new Subject<InterestRegisterListItem[]>();

        this.http
            .get<ResultData<ListInterestRegisterEntryApiRes[]>>(environment.localApi + 'InterestRegister/entries/mine')
            .subscribe({
                next: (res) => {
                    try {
                        const entries = res?.data ?? [];

                        subject.next(entries.map((entry) => this.mapListItem(entry)));
                        subject.complete();
                    } catch (error) {
                        subject.error(error);
                    }
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });

        return subject.asObservable();
    }

    public listAllEntries(): Observable<InterestRegisterListItem[]> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki skráningar');

        const subject = new Subject<InterestRegisterListItem[]>();

        this.http
            .get<ResultData<ListInterestRegisterEntryApiRes[]>>(environment.localApi + 'InterestRegister/entries')
            .subscribe({
                next: (res) => {
                    try {
                        const entries = res?.data ?? [];

                        subject.next(entries.map((entry) => this.mapListItem(entry)));
                        subject.complete();
                    } catch (error) {
                        subject.error(error);
                    }
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });

        return subject.asObservable();
    }

    public getEntry(id: string, canViewAll = false): Observable<InterestRegisterDetail | null> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki skráningu');

        const subject = new Subject<InterestRegisterDetail | null>();

        const url = canViewAll
            ? environment.localApi + 'InterestRegister/entries/' + id
            : environment.localApi + 'InterestRegister/entries/mine/' + id;

        this.http
            .get<ResultData<ReadInterestRegisterEntryApiRes>>(url)
            .subscribe({
                next: (res) => {
                    try {
                        const entry = res?.data ?? null;
                        subject.next(entry ? this.mapDetail(entry) : null);
                        subject.complete();
                    } catch (error) {
                        subject.error(error);
                    }
                },
                error: (error) => {
                    if (error?.status !== 401 && error?.status !== 403 && error?.status !== 404) {
                        HttpError(this.alertsService, this.router, error, '', '', '', '');
                    }

                    subject.error(error);
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });

        return subject.asObservable();
    }

    private uploadAttachments(entryGuid: string, files: File[]): Observable<InterestRegisterAttachmentApiRes[]> {
        const subject = new Subject<InterestRegisterAttachmentApiRes[]>();

        const formData = new FormData();

        files.forEach((file) => {
            formData.append('files', file, file.name);
        });

        this.http
            .post<
                ResultData<InterestRegisterAttachmentApiRes[]>
            >(environment.localApi + 'InterestRegister/entries/' + entryGuid + '/attachments', formData)
            .subscribe({
                next: (res) => {
                    subject.next(res?.data ?? []);
                    subject.complete();
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            });

        return subject.asObservable();
    }

    public downloadAttachment(entryGuid: string, attachmentGuid: string, fileName: string): Observable<void> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Sæki viðhengi');

        const subject = new Subject<void>();

        this.http
            .get(environment.localApi + 'InterestRegister/entries/' + entryGuid + '/attachments/' + attachmentGuid, {
                responseType: 'blob',
            })
            .subscribe({
                next: (blob) => {
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');

                    link.href = url;
                    link.download = fileName;
                    link.click();

                    window.URL.revokeObjectURL(url);

                    subject.next();
                    subject.complete();
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });

        return subject.asObservable();
    }

    public updateReviewed(entryGuid: string, isReviewed: boolean): Observable<boolean> {
        this.loaderService.changeActiveLoaders(1);
        this.loaderService.changeLoaderMessage('Uppfæri stöðu skráningar');

        const subject = new Subject<boolean>();

        this.http
            .post<ResultData<boolean>>(environment.localApi + 'InterestRegister/entries/' + entryGuid + '/review', { isReviewed })
            .subscribe({
                next: (res) => {
                    subject.next(res?.data ?? false);
                    subject.complete();
                },
                error: (error) => {
                    HttpError(this.alertsService, this.router, error, '', '', '', '');
                    subject.error(error);
                },
            })
            .add(() => {
                this.loaderService.changeActiveLoaders(-1);
            });

        return subject.asObservable();
    }

    public readAccess(): Observable<ReadInterestRegisterAccessApiRes> {
        const subject = new Subject<ReadInterestRegisterAccessApiRes>();

        this.http.get<ResultData<ReadInterestRegisterAccessApiRes>>(environment.localApi + 'InterestRegister/access').subscribe({
            next: (res) => {
                subject.next(res?.data ?? res?.returnItem ?? { canReadAll: false });
                subject.complete();
            },
            error: (error) => {
                HttpError(this.alertsService, this.router, error, '', '', '', '');
                subject.next({ canReadAll: false });
                subject.complete();
            },
        });

        return subject.asObservable();
    }

    private mapListItem(entry: ListInterestRegisterEntryApiRes): InterestRegisterListItem {
        const type = this.mapEntryTypeFromApiValue(entry.entryType);

        return {
            id: entry.entryGuid,
            type,
            typeLabel: this.getEntryTypeLabel(type),
            createdDate: entry.createdDate,
            createdByName: entry.createdByName ?? entry.createdBy?.toString() ?? '',
            status: entry.isReviewed ? InterestRegisterEntryStatus.Reviewed : InterestRegisterEntryStatus.Submitted,
            isReviewed: entry.isReviewed,
            hasAttachments: entry.hasAttachments,
        };
    }

    private mapDetail(entry: ReadInterestRegisterEntryApiRes): InterestRegisterDetail {
        const type = this.mapEntryTypeFromApiValue(entry.entryType);

        return {
            id: entry.entryGuid,
            type,
            typeLabel: this.getEntryTypeLabel(type),
            createdDate: entry.createdDate,
            createdByName: entry.createdByName ?? entry.createdBy?.toString() ?? '',
            status: entry.isReviewed ? InterestRegisterEntryStatus.Reviewed : InterestRegisterEntryStatus.Submitted,
            isReviewed: entry.isReviewed,
            reviewedDate: entry.reviewedDate,
            reviewedBy: entry.reviewedBy,
            reviewedByName: entry.reviewedByName ?? entry.reviewedBy?.toString() ?? '',
            hasAttachments: entry.attachments?.length > 0,
            attachments: entry.attachments?.map((attachment) => this.mapAttachment(attachment)) ?? [],
            formData: entry.formData,
        };
    }

    private mapAttachment(attachment: InterestRegisterAttachmentApiRes): InterestRegisterAttachment {
        return {
            id: attachment.attachmentGuid,
            fileName: attachment.fileName,
            fileSize: attachment.fileSizeBytes ?? 0,
            contentType: attachment.contentType,
        };
    }

    private mapEntryTypeToApiValue(type: InterestRegisterEntryType): number {
        switch (type) {
            case InterestRegisterEntryType.ConflictOfInterest:
                return 1;
            case InterestRegisterEntryType.GiftOrTravel:
                return 2;
            case InterestRegisterEntryType.EmploymentOrBoardParticipation:
                return 3;
            case InterestRegisterEntryType.SecuritiesTrading:
                return 4;
            case InterestRegisterEntryType.InterDepartmentTrading:
                return 5;
            default:
                throw new Error('Óþekkt tegund hagsmunaskráningar');
        }
    }

    private mapEntryTypeFromApiValue(type: number): InterestRegisterEntryType {
        switch (type) {
            case 1:
                return InterestRegisterEntryType.ConflictOfInterest;
            case 2:
                return InterestRegisterEntryType.GiftOrTravel;
            case 3:
                return InterestRegisterEntryType.EmploymentOrBoardParticipation;
            case 4:
                return InterestRegisterEntryType.SecuritiesTrading;
            case 5:
                return InterestRegisterEntryType.InterDepartmentTrading;
            default:
                return InterestRegisterEntryType.ConflictOfInterest;
        }
    }

    private getEntryTypeLabel(type: InterestRegisterEntryType): string {
        switch (type) {
            case InterestRegisterEntryType.ConflictOfInterest:
                return 'Hagsmunaárekstur';
            case InterestRegisterEntryType.GiftOrTravel:
                return 'Gjafir, boð og starfstengdar boðsferðir';
            case InterestRegisterEntryType.EmploymentOrBoardParticipation:
                return 'Atvinnuþátttaka, atvinnustarfsemi og stjórnarseta';
            case InterestRegisterEntryType.SecuritiesTrading:
                return 'Viðskipti með fjármálagerninga';
            case InterestRegisterEntryType.InterDepartmentTrading:
                return 'Viðskipti milli deilda LSR';
            default:
                return 'Hagsmunaskráning';
        }
    }

    private normalizeFormDataForSubmit(type: InterestRegisterEntryType, formData: Record<string, unknown>): Record<string, unknown> {
        const normalizedFormData = { ...formData };

        switch (type) {
            case InterestRegisterEntryType.GiftOrTravel:
                this.normalizeNumberField(normalizedFormData, 'estimatedValue');
                break;

            case InterestRegisterEntryType.EmploymentOrBoardParticipation:
                this.normalizeNumberField(normalizedFormData, 'compensation');
                break;

            case InterestRegisterEntryType.SecuritiesTrading:
                this.normalizeNumberField(normalizedFormData, 'nominalValue');
                this.normalizeNumberField(normalizedFormData, 'price');
                break;

            case InterestRegisterEntryType.InterDepartmentTrading:
                this.normalizeNumberField(normalizedFormData, 'nominalValue');
                this.normalizeNumberField(normalizedFormData, 'yieldOrPrice');
                break;

            default:
                break;
        }

        return normalizedFormData;
    }

    private normalizeNumberField(formData: Record<string, unknown>, key: string): void {
        if (!(key in formData)) {
            return;
        }

        const normalizedValue = this.parseIcelandicNumber(formData[key]);

        if (normalizedValue !== null) {
            formData[key] = normalizedValue;
        }
    }

    private parseIcelandicNumber(value: unknown): number | null {
        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        if (value === null || value === undefined) {
            return null;
        }

        let text = String(value).trim();

        if (!text) {
            return null;
        }

        text = text.replace(/\s/g, '').replace(/[^\d,.-]/g, '');

        if (!text) {
            return null;
        }

        if (text.includes(',')) {
            text = text.replace(/\./g, '').replace(',', '.');
        } else if (/^-?\d{1,3}(\.\d{3})+$/.test(text)) {
            text = text.replace(/\./g, '');
        }

        const numberValue = Number(text);

        return Number.isFinite(numberValue) ? numberValue : null;
    }

    private getAttachmentValidationError(files: File[]): string | null {
        if (!files || files.length === 0) {
            return null;
        }

        if (files.length > this.maxAttachmentCount) {
            return 'Hámark ' + this.maxAttachmentCount + ' viðhengi eru leyfð.';
        }

        for (const file of files) {
            if (!file) {
                return 'Viðhengi er ekki gilt.';
            }

            if (file.size === 0) {
                return 'Viðhengi má ekki vera tómt: ' + file.name;
            }

            if (file.size > this.maxAttachmentSizeBytes) {
                return 'Viðhengi er of stórt: ' + file.name;
            }

            const extension = this.getFileExtension(file.name);

            if (!extension || !this.allowedAttachmentExtensions.includes(extension)) {
                return 'Óleyfileg skráarending á viðhengi: ' + file.name;
            }
        }

        return null;
    }

    private getFileExtension(fileName: string): string {
        const lastDotIndex = fileName.lastIndexOf('.');

        if (lastDotIndex < 0) {
            return '';
        }

        return fileName.substring(lastDotIndex).toLowerCase();
    }
}
